import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Callout, Card, Heading, Text } from '../ui/index.js';
import type { Platform } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { useAccess } from '../api/hooks.js';
import { Focusable } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { useAdminGames } from '../components/admin/gameFields.js';
import { Breadcrumbs } from '../components/layout/Breadcrumbs.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { capabilities } from '../platform/index.js';
import { native, onNative, type JobProgress, type ScanResult } from '../platform/tauri.js';
import { formatBytes } from '../lib/format.js';
import { errorMessage } from '../lib/errors.js';
import { UPLOAD_PHASE_LABELS } from '../lib/uploadPhaseLabels.js';

export const Route = createFileRoute('/admin/games/$id/builds')({ component: AdminBuilds });

interface BuildRow {
  id: string;
  platform: Platform;
  status: string;
  version_label: string;
  total_bytes: number | null;
  new_bytes: number | null;
  created_at: number;
  published_at: number | null;
  error: string | null;
}

/**
 * The build screen (`03-client-app.md` section 4.5).
 *
 * Uploading needs the native content pipeline, so it only appears on Windows and Linux. There is
 * no build history: only the current build per platform is kept.
 */
function AdminBuilds() {
  const { id } = Route.useParams();
  const { isAdmin } = useAccess();
  // Only for the name in the trail. It is the list the editor already reads, so opening this
  // screen from there costs nothing and the title is there before the builds are.
  const games = useAdminGames();

  const builds = useQuery({
    queryKey: ['admin', 'games', id, 'builds'],
    queryFn: async () =>
      (await api.get<{ builds: BuildRow[] }>(`/admin/games/${id}/builds`)).builds,
    enabled: isAdmin,
    refetchInterval: 10_000,
  });

  if (!isAdmin) return <EmptyState title="You cannot do that." />;
  if (builds.isLoading) return <Loading />;

  const rows = builds.data ?? [];
  const current = rows.filter((b) => b.status === 'published');
  const inFlight = rows.filter((b) => b.status !== 'published');
  const game = games.data?.find((row) => row.id === id);

  return (
    <div className="gb-stack">
      {/* This screen carries no title of its own, so the trail is also the only thing on it
          that says which game's builds these are. */}
      <Breadcrumbs
        trail={[
          { label: 'Admin', link: { to: '/admin' } },
          { label: 'Games', link: { to: '/admin/games' } },
          {
            label: game?.title ?? 'Game',
            link: { to: '/admin/games/$id/edit', params: { id } },
          },
          { label: 'Builds' },
        ]}
      />

      <Text size="2" color="gray">
        Only the current build is kept. Publishing a new one replaces it.
      </Text>

      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">Current build</Heading>
        {current.length === 0 ? (
          <Text color="gray">None</Text>
        ) : (
          current.map((build) => (
            <div key={build.id} className="gb-seat">
              <Badge>{build.platform}</Badge>
              <Text size="2">{build.version_label}</Text>
              <span className="gb-seat__spacer" />
              <Text size="1" color="gray">
                {build.total_bytes ? formatBytes(build.total_bytes) : ''}
              </Text>
            </div>
          ))
        )}
      </Card>

      {inFlight.length > 0 ? (
        <Card size="3" className="gb-stack gb-stack--tight">
          {inFlight.map((build) => (
            <div key={build.id} className="gb-seat">
              <Badge color={build.status === 'failed' ? 'danger' : 'amber'}>{build.status}</Badge>
              <Text size="2">{build.version_label}</Text>
              <span className="gb-seat__spacer" />
              {build.error ? (
                <Text size="1" color="gray">
                  {build.error}
                </Text>
              ) : null}
              {build.status === 'ready' ? <PublishButton buildId={build.id} /> : null}
            </div>
          ))}
        </Card>
      ) : null}

      {capabilities.uploadBuilds ? (
        <UploadBuild gameId={id} />
      ) : (
        <Callout.Root>
          <Callout.Description>Get the app</Callout.Description>
        </Callout.Root>
      )}
    </div>
  );
}

function PublishButton({ buildId }: { buildId: string }) {
  const [busy, setBusy] = React.useState(false);
  return (
    <Focusable
      onSelect={async () => {
        setBusy(true);
        try {
          await api.post(`/admin/builds/${buildId}/publish`);
        } finally {
          setBusy(false);
        }
      }}
      selectLabel="Publish"
      disabled={busy}
    >
      <Button size="1" disabled={busy}>
        Publish
      </Button>
    </Focusable>
  );
}

/** Scan, then upload, with the delta report the spec asks for. */
function UploadBuild({ gameId }: { gameId: string }) {
  const [platform, setPlatform] = React.useState<Platform>('windows');
  const [path, setPath] = React.useState('');
  const [versionLabel, setVersionLabel] = React.useState(todayLabel());
  const [publishWhenReady, setPublishWhenReady] = React.useState(true);
  const [scan, setScan] = React.useState<ScanResult | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [progress, setProgress] = React.useState<JobProgress | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let off: (() => void) | null = null;
    void onNative('job://progress', (p) => {
      if (p.kind === 'upload_build') setProgress(p);
    }).then((stop) => {
      off = stop;
    });
    return () => off?.();
  }, []);

  const pick = async () => {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const picked = await open(
      platform === 'windows'
        ? { directory: true, multiple: false }
        : { multiple: false, filters: [{ name: 'APK', extensions: ['apk'] }] },
    );
    if (typeof picked === 'string') {
      setPath(picked);
      setScan(null);
    }
  };

  const runScan = async () => {
    setError(null);
    try {
      setScan(await native.uploadBuildScan(path, []));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const start = async () => {
    setError(null);
    try {
      setJobId(
        await native.uploadBuildStart({
          gameId,
          platform,
          versionLabel: versionLabel.trim(),
          path,
          publishWhenReady,
        }),
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const percent =
    progress && progress.bytesTotal > 0
      ? Math.round((progress.bytesDone / progress.bytesTotal) * 100)
      : 0;

  return (
    <Card size="3">
      <Heading size="4">Upload a build</Heading>

      <div className="gb-row">
        {(['windows', 'android'] as const).map((value) => (
          <Focusable key={value} onSelect={() => setPlatform(value)} selectLabel={value}>
            <Button size="2" variant={platform === value ? 'solid' : 'surface'}>
              {value}
            </Button>
          </Focusable>
        ))}
      </div>

      <div className="gb-row" style={{ marginTop: '0.5rem' }}>
        <Focusable
          onSelect={() => void pick()}
          selectLabel={platform === 'windows' ? 'Choose the build folder' : 'Choose the APK'}
        >
          <Button size="2" variant="surface">
            {platform === 'windows' ? 'Choose the build folder' : 'Choose the APK'}
          </Button>
        </Focusable>
        {path ? (
          <Text size="1" color="gray">
            {path}
          </Text>
        ) : null}
      </div>

      <FocusableTextField
        value={versionLabel}
        onChange={setVersionLabel}
        label="Version label"
        maxLength={64}
      />

      <div className="gb-row" style={{ marginTop: '0.5rem' }}>
        <Focusable onSelect={() => void runScan()} selectLabel="Scan" disabled={!path}>
          <Button size="2" variant="surface" disabled={!path}>
            Scan
          </Button>
        </Focusable>
        <Focusable onSelect={() => setPublishWhenReady((p) => !p)} selectLabel="Publish when ready">
          <Button size="2" variant={publishWhenReady ? 'solid' : 'surface'}>
            Publish when ready
          </Button>
        </Focusable>
        <Focusable
          onSelect={() => void start()}
          selectLabel="Upload"
          disabled={!scan || !versionLabel.trim()}
        >
          <Button size="2" disabled={!scan || !versionLabel.trim()}>
            Upload
          </Button>
        </Focusable>
      </div>

      {scan ? (
        <Text render={<p />} size="2" color="gray">
          {`${scan.fileCount} files, ${formatBytes(scan.totalBytes)}`}
          {scan.ignored.length > 0 ? ` · ${scan.ignored.length} files ignored` : ''}
        </Text>
      ) : null}

      {jobId && progress ? (
        <div className="gb-stack" style={{ marginTop: '0.75rem' }}>
          <Text size="2">
            {UPLOAD_PHASE_LABELS[progress.phase] ?? progress.phase} {percent}%
          </Text>
          <div className="gb-progress">
            <div className="gb-progress__bar" style={{ width: `${percent}%` }} />
          </div>
          {progress.newBytes !== undefined ? (
            <Text size="1" color="gray">
              {`New data: ${formatBytes(progress.newBytes)} of ${formatBytes(progress.bytesTotal)} (${
                progress.bytesTotal > 0
                  ? Math.round((progress.newBytes / progress.bytesTotal) * 100)
                  : 0
              }%)`}
            </Text>
          ) : null}
          <div className="gb-row">
            <Focusable onSelect={() => void native.uploadBuildPause(jobId)} selectLabel="Pause">
              <Button size="1" variant="surface">
                Pause
              </Button>
            </Focusable>
            <Focusable onSelect={() => void native.uploadBuildResume(jobId)} selectLabel="Resume">
              <Button size="1" variant="surface">
                Resume
              </Button>
            </Focusable>
            <Focusable onSelect={() => void native.uploadBuildCancel(jobId)} selectLabel="Cancel">
              <Button size="1" variant="ghost" color="danger">
                Cancel
              </Button>
            </Focusable>
          </div>
        </div>
      ) : null}

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}
    </Card>
  );
}

/** Version labels default to today, which is what a daily build wants. */
function todayLabel(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}.${pad(now.getMonth() + 1)}.${pad(now.getDate())}`;
}
